import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap, finalize, filter, take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { SessionService } from '../session.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { UserManagementService } from '../user-management/user-management.service';
import { BehaviorSubject } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

const TOKEN_HEADER_KEY = 'Authorization';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
  private sessionTimeoutWarningShown = false;
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);


  constructor(
    private authService: AuthService,
    private toastr: ToastrService,
    private router: Router,
    private sessionService: SessionService,
    private userService: UserManagementService,
    private modalService: NgbModal
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.sessionService.getToken();

    if (req.headers.get('No-Auth') === 'True') {
      return next.handle(req);
    }

    let authReq = req;
    if (token) {
      authReq = req.clone({
        headers: req.headers.set(TOKEN_HEADER_KEY, `Bearer ${token}`),
      });
    }

    return next.handle(authReq).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
          return this.handle401Error(authReq, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.sessionService.getRefreshToken();
      if (refreshToken) {
        return this.userService.refreshToken(refreshToken).pipe(
          switchMap((res: any) => {
            this.isRefreshing = false;
            this.sessionService.setToken(res.access_token);
            this.sessionService.setRefreshToken(res.refresh_token);
            this.refreshTokenSubject.next(res.access_token);
            // Retry the original request
            return next.handle(this.addToken(request, res.access_token));
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.sessionService.clear();
            this.modalService.dismissAll();
            this.router.navigate(['/login']);
            return throwError(() => err);
          })
        );
      } else {
        this.sessionService.clear();
        this.sessionService.emitCourse(false);
        this.router.navigate(['/login']);

        return throwError(() => 'No refresh token');
      }
    } else {
      // Wait until the token is refreshed, then retry the request
      return this.refreshTokenSubject.pipe(
        // filter waits until the token is set (not null)
        filter(token => token != null),
        take(1),
        switchMap((token) => {
          return next.handle(this.addToken(request, token!));
        })
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      headers: request.headers.set(TOKEN_HEADER_KEY, `Bearer ${token}`)
    });
  }

}
